import Figure from "@hydrologiq/shared/figures/generic/Figure"
import FigureContent from "@hydrologiq/shared/figures/generic/FigureContent"
import { AssetHeadlineFigure } from "@hydrologiq/gql-experience/graphql"
import { isCompactFigure } from "@hydrologiq/shared/figures/generic/figureVariant"

interface HydrogenProducedFigureProps {
  data?: AssetHeadlineFigure
  loading?: boolean
  error?: string
  isPlaceholder?: boolean
  compact?: boolean
}

/**
 * Formatting for produced value presentation: 
 * - 3 dp.
 * - >0 but less then 0.000 renders as "<0.000 unit" 
 * - If 0, renders as "0.000 unit".
 */
export function formatProduced(value: number, unit: string): string {
  if (value > 0 && value < 0.0005) return `<0.000 ${unit}`;
  return `${value.toFixed(3)} ${unit}`;
}

/**
 * Burnaston overview card — "Hydrogen Produced (so far today)".
 *
 * Value is the sum of produced across parent / non-child electrolyser assets for
 * the day (decided by product: Adam Ayre-Storie). It is computed in core and
 * delivered as an AssetHeadlineFigure via installationHeadlines — this component
 * only renders it.
 *
 * Units: display in kg. Produced telemetry is natively NL; convert with KG_PER_NL
 * (Enapter EL 4.1 factor — see ./hydrogenUnits.ts). The authoritative conversion runs
 * in core; this component renders the kg value core sends.
 */
function HydrogenProducedFigure({
  data,
  loading,
  error,
  isPlaceholder,
  compact = isCompactFigure("summary"),
}: HydrogenProducedFigureProps) {
  const value = data?.value ?? null
  const unit = data?.unit ?? "kg"

  return (
    <Figure
      heading={data?.label ?? "Hydrogen Produced"}
      loading={loading}
      error={error}
      isPlaceholder={isPlaceholder ?? !data?.available}
      compact={compact}
    >
      <FigureContent.Root>
        <FigureContent.SubText>So far today</FigureContent.SubText>
        <FigureContent.Value>
          {value != null ? `${formatProduced(value, unit)}` : "-"}
        </FigureContent.Value>
      </FigureContent.Root>
    </Figure>
  )
}

export default HydrogenProducedFigure
